import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { bankDetailsService } from '../../services/bankDetailsService';
import { toast } from 'react-toastify';

/**
 * Admin page for managing company bank details for manual transfers
 * @returns {JSX.Element} The BankDetailsPage component
 */
const BankDetailsPage = () => {
  const { t } = useTranslation('misc');
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountHolderName: '',
    iban: '',
    bic: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Copy to clipboard functionality
  const copyToClipboard = (text, feedbackMessage) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(feedbackMessage || t('common.copyToClipboard'));
      },
      (err) => {
        toast.error(t('common.copyFailed'));
        console.error('Could not copy text: ', err);
      },
    );
  };

  // Load bank details on component mount
  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await bankDetailsService.getBankDetails();

        // Check if data is not empty (bank details exist)
        if (data && data.id) {
          setBankDetails({
            bankName: data.bankName || '',
            accountHolderName: data.accountHolderName || '',
            iban: data.iban || '',
            bic: data.bic || '',
          });
        }
      } catch (err) {
        setError(err.message || t('bankDetails.errorLoading'));
        console.error('Error loading bank details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, [t]);

  // Replace useDocumentTitle with direct title update
  useEffect(() => {
    document.title = t('bankDetails.title');
    return () => {
      document.title = 'B2B E-Commerce';
    };
  }, [t]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBankDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear success message when form is edited
    if (success) setSuccess(false);
  };

  // Format IBAN with spaces for better readability
  const formatIBAN = (iban) => {
    // Remove all spaces first
    const cleanedIBAN = iban.replace(/\s/g, '');
    // Add a space every 4 characters
    return cleanedIBAN.replace(/(.{4})/g, '$1 ').trim();
  };

  // Save bank details
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!bankDetails.bankName || !bankDetails.accountHolderName || !bankDetails.iban) {
      setError(t('bankDetails.requiredFields'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Clean up IBAN before sending (remove spaces)
      const dataToSend = {
        ...bankDetails,
        iban: bankDetails.iban.replace(/\s/g, ''),
      };

      await bankDetailsService.updateBankDetails(dataToSend);
      setSuccess(true);
      toast.success(t('bankDetails.saveSuccess'));
    } catch (err) {
      setError(err.message || t('bankDetails.saveFailed'));
      toast.error(t('bankDetails.saveFailed'));
      console.error('Error saving bank details:', err);
    } finally {
      setSaving(false);
    }
  };

  // Preview what customers will see
  const generateInstructions = () => {
    if (!bankDetails.bankName || !bankDetails.accountHolderName || !bankDetails.iban) {
      return null;
    }

    return `${t('bankDetails.preview.bank')}: ${bankDetails.bankName}
${t('bankDetails.preview.accountHolder')}: ${bankDetails.accountHolderName}
IBAN: ${formatIBAN(bankDetails.iban)}${bankDetails.bic ? `\nBIC/SWIFT: ${bankDetails.bic}` : ''}
${t('bankDetails.preview.paymentReference')}: ORDER-NUMBER`;
  };

  const previewInstructions = generateInstructions();

  return (
    <Container
      maxWidth="md"
      sx={{ mt: 4, mb: 4 }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
      >
        {t('bankDetails.title')}
      </Typography>
      <Typography
        variant="body1"
        paragraph
      >
        {t('bankDetails.subtitle')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
            >
              {t('bankDetails.saveSuccess')}
            </Alert>
          )}

          <Paper
            elevation={2}
            sx={{ p: 3, mb: 4 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccountBalanceIcon sx={{ mr: 1 }} />
              <Typography variant="h6">{t('bankDetails.bankAccountInfo')}</Typography>
            </Box>

            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  required
                  fullWidth
                  label={t('bankDetails.fields.bankName')}
                  name="bankName"
                  value={bankDetails.bankName}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  helperText={t('bankDetails.fields.bankNameHelp')}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  required
                  fullWidth
                  label={t('bankDetails.fields.accountHolderName')}
                  name="accountHolderName"
                  value={bankDetails.accountHolderName}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  helperText={t('bankDetails.fields.accountHolderHelp')}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={8}
              >
                <TextField
                  required
                  fullWidth
                  label={t('bankDetails.fields.iban')}
                  name="iban"
                  value={bankDetails.iban}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  helperText={t('bankDetails.fields.ibanHelp')}
                  InputProps={{
                    startAdornment: !bankDetails.iban ? null : (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={() =>
                            copyToClipboard(
                              bankDetails.iban.replace(/\s/g, ''),
                              t('bankDetails.fields.ibanCopied'),
                            )
                          }
                          edge="start"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
              >
                <TextField
                  fullWidth
                  label={t('bankDetails.fields.bic')}
                  name="bic"
                  value={bankDetails.bic}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  helperText={t('bankDetails.fields.bicHelp')}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={
                  saving ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                    />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={saving}
                sx={{ mr: 2 }}
              >
                {saving ? t('bankDetails.actions.saving') : t('bankDetails.actions.save')}
              </Button>
            </Box>
          </Paper>

          {/* Preview section */}
          {previewInstructions && (
            <Paper
              elevation={2}
              sx={{ p: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ mr: 1 }} />
                <Typography variant="h6">{t('bankDetails.preview.title')}</Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{ mb: 2 }}
              >
                {t('bankDetails.preview.description')}
              </Typography>

              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: 'grey.50', mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  gutterBottom
                >
                  {t('bankDetails.preview.instructionsTitle')}
                </Typography>
                <Typography
                  variant="body2"
                  paragraph
                >
                  {t('bankDetails.preview.instructionsText')}
                </Typography>

                <Box
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    p: 2,
                    bgcolor: '#f9f9f9',
                    borderRadius: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {previewInstructions}
                </Box>
              </Paper>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<ContentCopyIcon />}
                  onClick={() =>
                    copyToClipboard(
                      previewInstructions,
                      t('bankDetails.actions.instructionsCopied'),
                    )
                  }
                  size="small"
                >
                  {t('bankDetails.actions.copyInstructions')}
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      )}
    </Container>
  );
};

export default BankDetailsPage;
